/**
 * ⭐ **TURNSTILE — `managed` mod.**
 *
 * ════════════════════════════════════════════════════════════════════════
 * 🚨 ÖNCEKİ HÂLİ HİÇ ÇALIŞMIYORDU
 * ════════════════════════════════════════════════════════════════════════
 * Üç ayrı eksik vardı ve üçü birlikte sessiz bir arıza üretiyordu:
 *
 * 1. **Script hiç yüklenmiyordu.** `index.html` içinde Turnstile etiketi yoktu;
 *    `window.turnstile` daima `undefined` kalıyordu.
 * 2. Bu yüzden istemci her seferinde `"dev-bypass"` gönderiyordu.
 * 3. Sunucu **fail-closed**: gerçek bir sır konduğu anda `"dev-bypass"`
 *    reddedilecek ve **hiçbir katılım kabul edilmeyecekti**.
 *
 * Yani "Turnstile anahtarını gir" yeterli değildi; anahtar girildiği an ürün
 * tamamen kapanırdı.
 *
 * ════════════════════════════════════════════════════════════════════════
 * ⚠️ `managed` MOD GÖRÜNÜR BİR KAP İSTER
 * ════════════════════════════════════════════════════════════════════════
 * Eski kod widget'ı `document.body`'ye eklenen **çıplak bir `<div>`** içinde
 * çiziyordu. `invisible` modda sorun değildi; `managed` modda Cloudflare
 * şüpheli durumda **etkileşimli bir kutucuk** gösterir ve o kutucuk sayfanın
 * en altında, formdan kopuk, muhtemelen ekran dışında belirirdi. Kullanıcı
 * "Katıl"a basar, hiçbir şey olmaz.
 *
 * Bu yüzden widget artık **formun içindeki** bir kaba çizilir ve çağıran taraf
 * o kabı sağlar.
 *
 * ⚠️ Üçüncü taraf script sayısı **sıfır** hedefinin (Rapor 14 §11) tek
 *    istisnasıdır: bir güvenlik kontrolüdür ve Cloudflare'in kendi alan
 *    adından gelir.
 */

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile";

/** ⚠️ Yükleme tek sefer denenir; her katılım denemesinde script eklenmez. */
let loader: Promise<TurnstileApi | null> | null = null;

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}

function api(): TurnstileApi | null {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile ?? null;
}

/**
 * Script'i **isteğe bağlı** yükler.
 *
 * ⚠️ Katılım sayfası açılır açılmaz değil, kullanıcı gerçekten katılmaya
 *    hazırlandığında yüklenir: kritik yol ölçümü (TTFF) üçüncü taraf bir
 *    script'in indirilmesiyle kirlenmemelidir.
 *
 * ⚠️ Yükleme başarısızlığı `null` döner, **fırlatmaz**. Çağıran taraf buna
 *    göre açık bir hata gösterir; ağ hatasını "doğrulama başarısız" diye
 *    sunmak kullanıcıyı yanlış yere bakmaya iter.
 */
export function loadTurnstile(): Promise<TurnstileApi | null> {
  const existing = api();
  if (existing) return Promise.resolve(existing);
  if (loader) return loader;

  loader = new Promise<TurnstileApi | null>((resolve) => {
    if (document.getElementById(SCRIPT_ID)) {
      resolve(api());
      return;
    }
    const el = document.createElement("script");
    el.id = SCRIPT_ID;
    el.src = SCRIPT_URL;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve(api());
    // ⚠️ `onerror` sessiz kalmaz: reklam engelleyici veya kurumsal güvenlik
    //    duvarı `challenges.cloudflare.com`'u kesmiş olabilir.
    el.onerror = () => resolve(null);
    document.head.appendChild(el);
  });
  return loader;
}

export type TurnstileOutcome =
  | { ok: true; token: string }
  | { ok: false; reason: "unconfigured" | "unreachable" | "failed" };

/**
 * Widget'ı verilen kaba çizer ve jetonu bekler.
 *
 * ⚠️ `siteKey` boş veya yer tutucu (`…`) ise **`unconfigured`** döner ve sahte
 *    bir jeton ÜRETMEZ. Eski kod `"dev-bypass"` döndürüyordu; bu, sunucu
 *    fail-closed olduğu için her katılımı sessizce reddettiriyordu ve
 *    hatanın nedeni hiçbir yerde görünmüyordu.
 */
export async function solveTurnstile(
  siteKey: string,
  host: HTMLElement,
): Promise<TurnstileOutcome> {
  if (siteKey.length === 0 || siteKey.includes("…")) {
    return { ok: false, reason: "unconfigured" };
  }

  const turnstile = await loadTurnstile();
  if (!turnstile) return { ok: false, reason: "unreachable" };

  return new Promise<TurnstileOutcome>((resolve) => {
    let widgetId: string | null = null;
    const cleanup = (): void => {
      if (widgetId !== null) {
        try {
          turnstile.remove(widgetId);
        } catch {
          /* zaten kaldırılmış olabilir */
        }
      }
    };

    widgetId = turnstile.render(host, {
      sitekey: siteKey,
      /**
       * ⚠️ `execution: "render"` + `appearance: "interaction-only"`:
       *    kutucuk YALNIZ gerçekten gerektiğinde görünür. `managed` modun
       *    amacı budur — normal kullanıcıya hiçbir sürtünme eklemez, şüpheli
       *    istekte doğrulama ister.
       */
      appearance: "interaction-only",
      callback: (token: string) => {
        cleanup();
        resolve({ ok: true, token });
      },
      "error-callback": () => {
        cleanup();
        resolve({ ok: false, reason: "failed" });
      },
      /**
       * ⚠️ Süresi dolan jeton **yenilenmez, hata döner**: kullanıcı formu
       *    doldururken 5 dakika beklerse jeton bayatlar ve sunucu reddeder.
       *    Sessizce yeniden denemek, "Katıl"ın bazen çalışıp bazen
       *    çalışmadığı bir arayüz üretirdi.
       */
      "expired-callback": () => {
        cleanup();
        resolve({ ok: false, reason: "failed" });
      },
    });
  });
}
